import { useMemo, useEffect, useState, useRef } from 'react';
import * as d3 from 'd3';
import { useChartDimensions } from './useChartDimensions';
import { Axis } from './Axis';
import { AxisY } from './AxisY';
import { roundTo } from './helpers';
import { gsap } from 'gsap';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// const fullLap = true;

const chartSettings = {
  marginLeft: 80,
  marginRight: 20,
  //   marginTop: 100,
  marginBottom: 30,
};
export const SingleLineChart = ({
  combinedStates,
  metric,
  unit = '%',
  min_value,
  enhancedTelemetryByLapNumber,
  fullLap,
}) => {
  const xAxis = 'Distance';
  const [filteredData, setFilteredData] = useState([]);
  const [currentTimeObject, setCurrentTimeObject] = useState();
  const [ref, dms] = useChartDimensions(chartSettings);
  const scrubby = useRef(null);

  const [yRange, setYrange] = useState([]);
  const [y2Range, setY2range] = useState([]);
  const [yAxis, setYaxis] = useState(null);
  const [yAxis2, setYaxis2] = useState(null);
  const [combined, setCombined] = useState(false);

  // Approach is to filter data object to appropriate lap and segment.
  useEffect(() => {
    const filterToCurrentSegment = () => {
      const lap = combinedStates.currLap;
      const seg = combinedStates.segment;
      // check in the telemetry metaData what the corresponding lap is
      const telemetryLapNumber =
        enhancedTelemetryByLapNumber?.metaData?.RunLapMapping?.find(
          (mapping) => mapping.RunLapNumber === lap
        )?.TelemetryLapNumber || null;

      if (seg && telemetryLapNumber) {
        if (fullLap) {
          setFilteredData(
            enhancedTelemetryByLapNumber?.laps?.[telemetryLapNumber]
          );
        } else {
          let prefilteredData = enhancedTelemetryByLapNumber?.laps?.[
            telemetryLapNumber
          ].filter((d) => d.SegmentNumber === seg);
          prefilteredData = prefilteredData.length > 0 ? prefilteredData : [];
          // prefilteredData.length > 0 ? prefilteredData : [{ fake: 'fake' }];
          setFilteredData(prefilteredData);
        }
      } else {
        // console.log('couldnt filter data to lap and segment');
      }
    };
    filterToCurrentSegment();
  }, [
    combinedStates.currLap,
    combinedStates.segment,
    enhancedTelemetryByLapNumber,
    fullLap,
  ]);

  //// Setup Axis
  useEffect(() => {
    if (metric.length === 1) {
      setYaxis(metric[0]);
    } else if (metric.length === 2) {
      setCombined(true);
      setYaxis(metric[0]);
      setYaxis2(metric[1]);
    } else {
      console.log('more than 2 metrics not currently supported');
    }
  }, [metric]);

  // find min and max values for the metric across all laps and segments. Do it once.
  useEffect(() => {
    const allValues = Object.values(enhancedTelemetryByLapNumber?.laps).flatMap(
      (lap) => lap.map((entry) => entry[yAxis])
    );
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    setYrange([minValue, maxValue]);
    if (combined) {
      const allValues = Object.values(
        enhancedTelemetryByLapNumber?.laps
      ).flatMap((lap) => lap.map((entry) => entry[yAxis2]));
      const minValue = Math.min(...allValues);
      const maxValue = Math.max(...allValues);
      setY2range([minValue, maxValue]);
    }
  }, [enhancedTelemetryByLapNumber.laps, yAxis, combined, yAxis2]);

  // X axis
  const [xMin, xMax] = d3.extent(filteredData, (d) => Number(d[xAxis]));
  const xScale = useMemo(
    () => d3.scaleLinear().domain([xMin, xMax]).range([0, dms.boundedWidth]),
    [dms.boundedWidth, xMin, xMax]
  );
  // Y axis
  let yMin, yMax, yMin2, yMax2, yAxisMax, yAxis2Max;
  if (combined) {
    [yMin, yMax] = yRange;
    [yMin2, yMax2] = y2Range;
    yAxisMax = 100;
    yAxis2Max = 100;
  } else {
    [yMin, yMax] = yRange;
    yAxisMax = yMax;
    yAxis2Max = yMax2;
  }
  const v = isNaN(min_value) ? yMin : min_value;
  // range array is reversed for Y axis
  // second value of domain can either be 'max' or 100 if using percentage
  const yScale = useMemo(
    () => d3.scaleLinear().domain([v, yAxisMax]).range([dms.boundedHeight, 0]),
    [dms.boundedHeight, yAxisMax, v]
  );
  const yScale2 = useMemo(
    () => d3.scaleLinear().domain([v, yAxis2Max]).range([dms.boundedHeight, 0]),
    [dms.boundedHeight, yAxis2Max, v]
  );

  // get object for current elapsed time
  useEffect(() => {
    const currentTime = combinedStates.currLapElapsedTime
      ? Number(roundTo(combinedStates.currLapElapsedTime, 1))
      : 0;
    const timeObject = filteredData.find((el) => el.Time === currentTime);
    setCurrentTimeObject(timeObject);
  }, [combinedStates.currLapElapsedTime, filteredData]);

  // animates marker line
  useEffect(() => {
    gsap.to(scrubby.current, {
      x: xScale(combinedStates.segmentDistance),
      ease: 'none',
      duration: 0,
    });
  }, [combinedStates.segmentDistance, xScale]);

  // Build the line - so gross
  let lineBuilder, linePath, lineBuilder2, linePath2;
  if (filteredData.length > 0) {
    if (combined) {
      lineBuilder = d3
        .line()
        .curve(d3.curveBasis)
        .x((d) => xScale(Number(d[xAxis])))
        .y((d) => yScale((Number(d[yAxis]) / yMax) * 100));
      linePath = lineBuilder(filteredData);
      if (linePath.includes('NaN')) {
        console.log('NaN in linePath for metric', yAxis);
      }
      lineBuilder2 = d3
        .line()
        .curve(d3.curveBasis)
        .x((d) => xScale(Number(d[xAxis])))
        .y((d) => yScale2((Number(d[yAxis2]) / yMax2) * 100));
      linePath2 = lineBuilder2(filteredData);
      if (linePath2.includes('NaN')) {
        console.log('NaN in linePath2 for metric', yAxis);
      }
    } else {
      lineBuilder = d3
        .line()
        .curve(d3.curveBasis)
        .x((d) => xScale(Number(d[xAxis])))
        .y((d) => yScale(Number(d[yAxis])));
      linePath = lineBuilder(filteredData);
      if (linePath.includes('NaN')) {
        console.log('NaN in linePath for metric', yAxis);
      }
    }
  }
  return (
    <>
      <Box
        sx={{
          border: '2px solid gray',
          borderRadius: '6px',
        }}
      >
        <Box
          sx={{
            borderBottom: '2px solid gray',
            height: '30px',
            display: 'flex',
            flexDirection: 'row',
            textAlign: 'center',
          }}
        >
          {!combined && (
            <Box
              sx={{
                // border: '2px solid blue',
                marginLeft: '10px',
                display: 'flex',
                flexDirection: 'row',
                textAlign: 'center',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography>{metric}:</Typography>
              </Box>
              <Box sx={{ marginLeft: '10px', marginRight: '1px' }}>
                <Typography>
                  {currentTimeObject
                    ? roundTo(Number(currentTimeObject[metric]), 2)
                    : 0}
                </Typography>
              </Box>
              <Box>
                <Typography>{unit}</Typography>
              </Box>
            </Box>
          )}
          {combined && (
            <Box
              sx={{
                // border: '2px solid green',
                marginLeft: '10px',
                display: 'flex',
                flexDirection: 'row',
                textAlign: 'center',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography>{yAxis}:</Typography>
              </Box>
              <Box sx={{ marginLeft: '10px', marginRight: '1px' }}>
                <Typography>
                  {currentTimeObject
                    ? roundTo((currentTimeObject[yAxis] / yMax) * 100, 1)
                    : 0}
                </Typography>
              </Box>
              <Box>
                <Typography>{unit}</Typography>
              </Box>
              <Box sx={{ marginLeft: '10px' }}>
                <Typography>{yAxis2}:</Typography>
              </Box>
              <Box sx={{ marginLeft: '10px', marginRight: '1px' }}>
                <Typography>
                  {currentTimeObject
                    ? roundTo((currentTimeObject[yAxis2] / yMax2) * 100, 1)
                    : 0}
                </Typography>
              </Box>
              <Box>
                <Typography>{unit}</Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Chart */}
        <Box
          ref={ref}
          sx={{
            height: '160px',
            maxWidth: '800px',
            minWidth: '400px',
            // border: '2px solid green',
            flexGrow: 2,
          }}
        >
          <svg width={dms.width} height={dms.height} fill="orange">
            <g
              transform={`translate(${[dms.marginLeft, dms.marginTop].join(
                ','
              )})`}
            >
              <path
                d={linePath}
                opacity={1}
                stroke="#9a6fb0"
                fill="none"
                strokeWidth={2}
              />
              {combined && (
                <path
                  d={linePath2}
                  opacity={1}
                  stroke="#aaaaaa"
                  fill="none"
                  strokeWidth={2}
                />
              )}
              {/* The marker line */}
              <path
                ref={scrubby}
                fill="none"
                d={['M', 0, 0, 'L', 0, dms.boundedHeight].join(' ')}
                stroke="#2979ff"
                strokeWidth="2"
                zindex="100"
              ></path>
              <g transform={`translate(${[0, 0].join(',')})`}>
                <AxisY
                  domain={yScale.domain()}
                  range={yScale.range()}
                  unit={unit}
                />
              </g>
              <g transform={`translate(${[0, dms.boundedHeight].join(',')})`}>
                <Axis domain={xScale.domain()} range={xScale.range()} />
              </g>
            </g>
          </svg>
        </Box>
      </Box>
    </>
  );
};
export default SingleLineChart;
